/**
 * Shared toast + confirm primitives for every Rynora React app.
 *
 * Why this lives in `@rynora/theme`:
 *   The toast + confirm UX (animation, colour palette, focus management,
 *   keyboard handling) is part of the Rynora design system. Putting it
 *   alongside `BrandMark`, `ThemeToggle`, and the design tokens keeps
 *   visual consistency across apps with zero copy-paste.
 *
 * Apps consume it like this:
 *
 *   // main.tsx
 *   import { AppDialogsProvider } from '@rynora/theme';
 *   ...
 *   <AppDialogsProvider>
 *     <App />
 *   </AppDialogsProvider>
 *
 *   // any component
 *   import { useAppDialogs, TOASTS, CONFIRM } from '@rynora/theme';
 *   const { toast, confirm } = useAppDialogs();
 *   toast.success(TOASTS.FlowSaved);
 *   confirm(CONFIRM.DeleteFlow, () => deleteFlow(id));
 *
 * The `confirm()` API accepts EITHER a structured `ConfirmCopy` object
 * (preferred — comes from `@rynora/messages`) OR a plain string for
 * back-compatibility with older call-sites that haven't migrated yet.
 */

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

// ---- Types ---------------------------------------------------------------

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  readonly id: number;
  readonly type: ToastType;
  readonly message: string;
}

export interface ToastApi {
  (message: string, type?: ToastType): void;
  success: (message: string) => void;
  error: (message: string) => void;
  warning: (message: string) => void;
  info: (message: string) => void;
}

export type ConfirmTone = 'danger' | 'warning' | 'primary';

/**
 * Structured confirmation copy. This shape mirrors the entries in
 * `@rynora/messages`/`CONFIRM` so call-sites can pass the constant
 * directly.
 */
export interface ConfirmCopy {
  readonly title: string;
  readonly body: string;
  readonly confirmLabel: string;
  readonly cancelLabel?: string;
  readonly tone?: ConfirmTone;
}

interface ConfirmState extends ConfirmCopy {
  readonly onConfirm: () => void;
}

interface DialogsApi {
  readonly toast: ToastApi;
  /**
   * Open a confirm dialog. Pass a structured `ConfirmCopy` object from
   * `@rynora/messages` or, for legacy call-sites, a plain string body
   * + optional title.
   */
  readonly confirm: (copy: ConfirmCopy | string, onConfirm: () => void, legacyTitle?: string) => void;
}

// ---- Context -------------------------------------------------------------

const noopToast = (() => {
  const fn = ((_msg: string, _type?: ToastType) => {}) as ToastApi;
  fn.success = () => {};
  fn.error = () => {};
  fn.warning = () => {};
  fn.info = () => {};
  return fn;
})();

const DialogsContext = createContext<DialogsApi>({
  toast: noopToast,
  confirm: () => {},
});

/** Read the dialogs API from anywhere inside `<AppDialogsProvider>`. */
export function useAppDialogs(): DialogsApi {
  return useContext(DialogsContext);
}

// ---- Internal helpers ----------------------------------------------------

let _toastId = 0;

function normaliseConfirm(copy: ConfirmCopy | string, legacyTitle?: string): ConfirmCopy {
  if (typeof copy === 'string') {
    return {
      title: legacyTitle || 'Confirm',
      body: copy,
      confirmLabel: 'Confirm',
      cancelLabel: 'Cancel',
      tone: 'primary',
    };
  }
  return copy;
}

// ---- Provider ------------------------------------------------------------

export interface AppDialogsProviderProps {
  readonly children: ReactNode;
  /**
   * How long (ms) toasts stay on screen before auto-dismissing. Default 3500.
   */
  readonly toastDurationMs?: number;
  /**
   * Optional render-prop allowing apps to inject extra app-specific
   * dialogs (e.g. flow-builder's plan-limit upgrade modal). Receives the
   * shared dialogs API and renders into the same overlay layer.
   */
  readonly extraOverlays?: (api: DialogsApi) => ReactNode;
}

export function AppDialogsProvider({ children, toastDurationMs = 3500, extraOverlays }: AppDialogsProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);

  const toast = useCallback<ToastApi>(
    ((message: string, type: ToastType = 'info') => {
      const id = ++_toastId;
      setToasts((prev) => [...prev, { id, type, message }]);
      setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), toastDurationMs);
    }) as ToastApi,
    [toastDurationMs],
  );

  // Mutating `toast` is fine — it's recreated only when toastDurationMs changes.
  toast.success = (message: string) => toast(message, 'success');
  toast.error = (message: string) => toast(message, 'error');
  toast.warning = (message: string) => toast(message, 'warning');
  toast.info = (message: string) => toast(message, 'info');

  const confirm = useCallback((copy: ConfirmCopy | string, onConfirm: () => void, legacyTitle?: string) => {
    const normalised = normaliseConfirm(copy, legacyTitle);
    setConfirmState({ ...normalised, onConfirm });
  }, []);

  // Esc closes any open confirm dialog. We don't trap focus here because
  // the dialog is small and the markup is keyboard-reachable; trapping
  // belongs in a dedicated <Modal> primitive.
  useEffect(() => {
    if (!confirmState) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setConfirmState(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirmState]);

  const dismissToast = (id: number) => setToasts((prev) => prev.filter((t) => t.id !== id));

  const handleConfirm = () => {
    confirmState?.onConfirm();
    setConfirmState(null);
  };

  const api: DialogsApi = { toast, confirm };
  const tone = confirmState?.tone ?? 'primary';
  const confirmButtonClass =
    tone === 'danger' ? 'btn btn-danger' : tone === 'warning' ? 'btn btn-warning' : 'btn btn-primary';

  return (
    <DialogsContext.Provider value={api}>
      {children}

      {/* Toast container — styled by the global tokens.css/app stylesheets. */}
      <div className="toast-container" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.type}`}>
            <span className={`toast-icon toast-icon-${t.type}`} aria-hidden="true" />
            <span className="toast-message">{t.message}</span>
            <button type="button" className="toast-close" onClick={() => dismissToast(t.id)} aria-label="Dismiss">
              ×
            </button>
          </div>
        ))}
      </div>

      {/* Confirm dialog */}
      {confirmState && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={confirmState.title}
          onClick={() => setConfirmState(null)}
        >
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className={`confirm-icon confirm-icon-${tone}`} aria-hidden="true" />
            <h3 className="confirm-title">{confirmState.title}</h3>
            <p className="confirm-message">{confirmState.body}</p>
            <div className="confirm-actions">
              <button type="button" className="btn" onClick={() => setConfirmState(null)}>
                {confirmState.cancelLabel ?? 'Cancel'}
              </button>
              <button type="button" className={confirmButtonClass} onClick={handleConfirm} autoFocus>
                {confirmState.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      {extraOverlays?.(api)}
    </DialogsContext.Provider>
  );
}
